/**
 * Dev-only Marketing page: preview recipe ad card and export at social-media dimensions.
 * Navigate to /dev/marketing to use.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import MarketingPriceDetails from '../components/MarketingPriceDetails';
import { calculateRecipeNutrition, formatNutritionValue } from '../utils/nutritionCalculator';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/marketing.css';

const DEFAULT_IMAGE = 'https://via.placeholder.com/400x300?text=No+Image';

// Social media format definitions (logical px at 1x; exported at 2x for high-res)
const FORMATS = [
  { id: 'square',   label: 'Square 1:1',       subtitle: 'Facebook / Instagram post', width: 1080, height: 1080 },
  { id: 'portrait', label: 'Portrait 4:5',      subtitle: 'Instagram feed portrait',   width: 1080, height: 1350 },
  { id: 'story',    label: 'Story / Reel 9:16', subtitle: 'Instagram & Facebook story',width: 1080, height: 1920 },
];

export default function MarketingRecipe() {
  const navigate = useNavigate();
  const exportRef = useRef(null);
  const [recipeList, setRecipeList] = useState([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [recipe, setRecipe] = useState(null);
  const [nutritionData, setNutritionData] = useState(null);
  const [recipeCosts, setRecipeCosts] = useState(null);
  const [ingredientPrices, setIngredientPrices] = useState({});
  const [loadingList, setLoadingList] = useState(true);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const [loadingCost, setLoadingCost] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [excludedIngredientNames, setExcludedIngredientNames] = useState([]);
  const [formatId, setFormatId] = useState('square');

  const activeFormat = FORMATS.find((f) => f.id === formatId) || FORMATS[0];

  // Fetch recipe list for dropdown
  useEffect(() => {
    const fetchList = async () => {
      try {
        setLoadingList(true);
        const res = await axios.get(`${API_BASE_URL}/recipes/search`, { params: { limit: 200 } });
        const list = Array.isArray(res.data) ? res.data : res.data?.recipes || [];
        setRecipeList(list);
        if (list.length > 0 && !selectedRecipeId) setSelectedRecipeId(list[0]._id);
      } catch (err) {
        console.error('Error fetching recipe list:', err);
        setRecipeList([]);
      } finally {
        setLoadingList(false);
      }
    };
    fetchList();
  }, []);

  // When selection changes, load full recipe
  useEffect(() => {
    if (!selectedRecipeId) {
      setRecipe(null); setNutritionData(null); setRecipeCosts(null);
      setIngredientPrices({}); return;
    }
    const loadRecipe = async () => {
      try {
        setLoadingRecipe(true);
        const res = await axios.get(`${API_BASE_URL}/recipes/${selectedRecipeId}`);
        setRecipe(res.data);
        setNutritionData(null); setRecipeCosts(null); setIngredientPrices({});
        setExcludedIngredientNames([]);
      } catch (err) {
        console.error('Error fetching recipe:', err);
        setRecipe(null);
      } finally {
        setLoadingRecipe(false);
      }
    };
    loadRecipe();
  }, [selectedRecipeId]);

  // Load nutrition
  useEffect(() => {
    if (!recipe || !recipe.ingredients?.length) return;
    let cancelled = false;
    const run = async () => {
      try {
        const nutrition = await calculateRecipeNutrition(
          recipe._id,
          recipe.ingredients.map((ing) => ({ ...ing, amount: ing.amount })),
          recipe.servings || 1
        );
        if (!cancelled) setNutritionData(nutrition);
      } catch { if (!cancelled) setNutritionData(null); }
    };
    run();
    return () => { cancelled = true; };
  }, [recipe?._id, recipe?.ingredients, recipe?.servings]);

  // Load cost
  const loadCost = async () => {
    if (!recipe) return;
    try {
      setLoadingCost(true);
      const servings = recipe.servings || 1;
      const scaledIngredients = recipe.ingredients.map((ing) => ({
        ...ing,
        amount: parseFloat(((ing.amount * servings) / (recipe.servings || 1)).toFixed(2))
      }));
      const res = await axios.post(`${API_BASE_URL}/recipes/${recipe._id}/calculate-cost`, {
        ingredients: scaledIngredients,
        userId: window.localStorage.getItem('userID')
      });
      const prices = res.data.ingredientPrices || {};
      setIngredientPrices(prices);
      const derivedTotal = Object.values(prices).reduce((sum, data) => {
        const brand = data?.selectedBrand || data?.availableBrands?.[0];
        return sum + (brand ? parseFloat(brand.cost) || 0 : 0);
      }, 0);
      setRecipeCosts(derivedTotal);
    } catch (err) {
      console.error('Error fetching cost:', err);
    } finally {
      setLoadingCost(false);
    }
  };

  useEffect(() => {
    if (recipe && recipe.ingredients?.length) loadCost();
  }, [recipe?._id]);

  // ── Canvas-based export ──────────────────────────────────────────────────
  // Fetches an image URL via the server proxy and resolves to an HTMLImageElement
  const loadProxiedImage = (src) => new Promise((resolve) => {
    if (!src) { resolve(null); return; }
    fetch(`/api/image-proxy?url=${encodeURIComponent(src)}`)
      .then((r) => r.ok ? r.blob() : null)
      .then((blob) => {
        if (!blob) { resolve(null); return; }
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
      })
      .catch(() => resolve(null));
  });

  // Draw an image centred-cropped into a rectangle
  const drawCoverImage = (ctx, img, x, y, w, h) => {
    if (!img) return;
    const scale = Math.max(w / img.width, h / img.height);
    const sw = img.width  * scale;
    const sh = img.height * scale;
    ctx.drawImage(img, x + (w - sw) / 2, y + (h - sh) / 2, sw, sh);
  };

  // Wrap text and return lines
  const wrapText = (ctx, text, maxWidth) => {
    const words = (text || '').split(' ');
    const lines = [];
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  const handleExport = async () => {
    if (!recipe) return;
    setExporting(true);
    try {
      const { width: W, height: H } = activeFormat;
      const PAD = Math.round(W * 0.045);   // ~48px at 1080
      const INNER = W - PAD * 2;

      // ── 1. Decide layout dimensions ──────────────────────────────────────
      // Hero image height depends on format
      const heroH = formatId === 'story'   ? Math.round(H * 0.52)
                  : formatId === 'portrait' ? Math.round(H * 0.44)
                  :                           Math.round(H * 0.40); // square

      // Ingredient cards: always show up to 4, in a 2×2 or 4×1 row
      const includedIngredients = scaledIngredients.filter(
        (ing) => !excludedSet.has(ing.name) && (ingredientPrices[ing.name]?.selectedBrand || ingredientPrices[ing.name]?.availableBrands?.[0])
      ).slice(0, 4);

      const COLS = 4;
      const GAP  = Math.round(W * 0.02);
      const CARD_W = Math.round((INNER - GAP * (COLS - 1)) / COLS);
      const CARD_H = Math.round(CARD_W * 1.55); // photo + text

      // ── 2. Load all images in parallel ───────────────────────────────────
      const recipeImgSrc = recipe.imageUrl
        ? (recipe.imageUrl.includes('cloudinary.com')
            ? recipe.imageUrl.replace('/upload/', '/upload/q_auto,f_auto/')
            : recipe.imageUrl)
        : null;

      const [heroImg, ...ingredientImgs] = await Promise.all([
        loadProxiedImage(recipeImgSrc),
        ...includedIngredients.map((ing) => {
          const data = ingredientPrices[ing.name];
          const brand = data?.selectedBrand || data?.availableBrands?.[0];
          return loadProxiedImage(brand?.imageUrl);
        }),
      ]);

      // ── 3. Build canvas ───────────────────────────────────────────────────
      const canvas = document.createElement('canvas');
      canvas.width  = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');

      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);

      let curY = 0;

      // ── Hero image ────────────────────────────────────────────────────────
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, W, heroH);
      ctx.clip();
      drawCoverImage(ctx, heroImg, 0, 0, W, heroH);
      // Gradient overlay at bottom of hero
      const grad = ctx.createLinearGradient(0, heroH * 0.55, 0, heroH);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, 'rgba(0,0,0,0.55)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, heroH);
      ctx.restore();
      curY = heroH;

      // Recipe name on top of hero gradient
      const nameFontSize = Math.round(W * 0.042);
      const sfSize = Math.round(W * 0.026);
      const sfGap  = Math.round(W * 0.012);   // gap between name and servings
      const sfBlockH = recipe.servings ? sfSize + sfGap : 0;
      const bottomPad = Math.round(W * 0.03);

      ctx.font = `800 ${nameFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      ctx.fillStyle = '#ffffff';
      ctx.textBaseline = 'top';
      const nameLines = wrapText(ctx, recipe.name, INNER);
      const nameLineH = nameFontSize * 1.25;
      const nameBlockH = nameLines.length * nameLineH;

      // Start name so that name + gap + servings sit above the bottom padding
      let nameY = heroH - bottomPad - sfBlockH - nameBlockH;
      for (const line of nameLines) {
        ctx.fillText(line, PAD, nameY);
        nameY += nameLineH;
      }
      // Servings — drawn below the last name line
      if (recipe.servings) {
        ctx.font = `400 ${sfSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.fillStyle = 'rgba(255,255,255,0.82)';
        ctx.fillText(`${recipe.servings} servings`, PAD, nameY + sfGap);
      }

      curY += Math.round(W * 0.035);

      // ── Nutrition strip ───────────────────────────────────────────────────
      if (nutritionData?.perServing) {
        const nutLabelSize = Math.round(W * 0.022);
        const nutValSize   = Math.round(W * 0.036);
        const nutLineH     = nutValSize * 1.3;
        const nutBlockH    = nutLabelSize + nutLineH + Math.round(W * 0.018);

        // Section label
        ctx.font = `700 ${nutLabelSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.fillStyle = '#64748b';
        ctx.textBaseline = 'top';
        ctx.fillText('NUTRITION PER SERVING', PAD, curY);
        curY += nutLabelSize + Math.round(W * 0.012);

        const nutItems = [
          { label: 'CALORIES', value: formatNutritionValue('Calories', nutritionData.perServing.Calories) },
          { label: 'PROTEIN',  value: formatNutritionValue('Protein',  nutritionData.perServing.Protein)  },
          { label: 'CARBS',    value: formatNutritionValue('Carbs',    nutritionData.perServing.Carbs)    },
          { label: 'FATS',     value: formatNutritionValue('Fats',     nutritionData.perServing.Fats)     },
        ];
        const nutColW = INNER / nutItems.length;
        for (let i = 0; i < nutItems.length; i++) {
          const nx = PAD + i * nutColW;
          ctx.font = `800 ${nutValSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
          ctx.fillStyle = '#0f172a';
          ctx.textBaseline = 'top';
          ctx.fillText(nutItems[i].value, nx, curY);
          ctx.font = `600 ${nutLabelSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
          ctx.fillStyle = '#64748b';
          ctx.fillText(nutItems[i].label, nx, curY + nutValSize + 4);
        }
        curY += nutValSize + nutLabelSize + Math.round(W * 0.025);

        // Divider
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(PAD, curY);
        ctx.lineTo(W - PAD, curY);
        ctx.stroke();
        curY += Math.round(W * 0.025);
      }

      // ── Cost summary ──────────────────────────────────────────────────────
      if (displayTotal != null) {
        const costLabelSize = Math.round(W * 0.028);
        const costAmtSize   = Math.round(W * 0.048);
        const costPerSize   = Math.round(W * 0.026);

        ctx.font = `700 ${costLabelSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.fillStyle = '#475569';
        ctx.textBaseline = 'top';
        ctx.fillText('Total cost:', PAD, curY);

        const labelW = ctx.measureText('Total cost:').width + Math.round(W * 0.018);
        ctx.font = `800 ${costAmtSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.fillStyle = '#0f172a';
        ctx.fillText(`R${Number(displayTotal).toFixed(2)}`, PAD + labelW, curY - Math.round((costAmtSize - costLabelSize) / 2));

        const amtW = ctx.measureText(`R${Number(displayTotal).toFixed(2)}`).width;
        if (includedCount > 0) {
          ctx.font = `400 ${costPerSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
          ctx.fillStyle = '#64748b';
          ctx.fillText(`(R${(displayTotal / servings).toFixed(2)}/serving)`,
            PAD + labelW + amtW + Math.round(W * 0.012),
            curY + Math.round((costLabelSize - costPerSize) / 2));
        }
        curY += costAmtSize + Math.round(W * 0.03);
      }

      // ── Ingredient cards (up to 4) ────────────────────────────────────────
      if (includedIngredients.length > 0) {
        const sectionLabelSize = Math.round(W * 0.022);
        ctx.font = `700 ${sectionLabelSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
        ctx.fillStyle = '#64748b';
        ctx.textBaseline = 'top';
        ctx.fillText('INGREDIENTS', PAD, curY);
        curY += sectionLabelSize + Math.round(W * 0.015);

        const photoH   = CARD_W;  // square photo
        const textPad  = Math.round(CARD_W * 0.08);
        const nameSize = Math.round(W * 0.024);
        const subSize  = Math.round(W * 0.02);
        const priceSize = Math.round(W * 0.028);
        const lineH    = subSize * 1.4;
        const RADIUS   = Math.round(CARD_W * 0.06);

        for (let i = 0; i < includedIngredients.length; i++) {
          const ing  = includedIngredients[i];
          const data = ingredientPrices[ing.name];
          const brand = data?.selectedBrand || data?.availableBrands?.[0];
          if (!brand) continue;

          const cx = PAD + i * (CARD_W + GAP);
          const cy = curY;

          // Card background with rounded corners
          ctx.fillStyle = '#f8fafc';
          ctx.beginPath();
          ctx.roundRect(cx, cy, CARD_W, CARD_H, RADIUS);
          ctx.fill();
          ctx.strokeStyle = '#e2e8f0';
          ctx.lineWidth = 1;
          ctx.stroke();

          // Product photo (clipped to top of card)
          ctx.save();
          ctx.beginPath();
          ctx.roundRect(cx, cy, CARD_W, photoH, [RADIUS, RADIUS, 0, 0]);
          ctx.clip();
          if (ingredientImgs[i]) {
            drawCoverImage(ctx, ingredientImgs[i], cx, cy, CARD_W, photoH);
          } else {
            ctx.fillStyle = '#e2e8f0';
            ctx.fillRect(cx, cy, CARD_W, photoH);
          }
          ctx.restore();

          // Ingredient name
          let ty = cy + photoH + textPad;
          ctx.font = `700 ${nameSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
          ctx.fillStyle = '#0f172a';
          ctx.textBaseline = 'top';
          const nameW = CARD_W - textPad * 2;
          const ingNameLines = wrapText(ctx, ing.name, nameW);
          for (const ln of ingNameLines.slice(0, 2)) {
            ctx.fillText(ln, cx + textPad, ty);
            ty += nameSize * 1.25;
          }

          // Product / store
          const productStr = [brand.name, brand.brand].filter(Boolean).join(' · ');
          ctx.font = `400 ${subSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
          ctx.fillStyle = '#64748b';
          const prodLines = wrapText(ctx, productStr, nameW);
          for (const ln of prodLines.slice(0, 2)) {
            ctx.fillText(ln, cx + textPad, ty);
            ty += lineH;
          }

          // Price
          ctx.font = `800 ${priceSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
          ctx.fillStyle = '#0f172a';
          ctx.fillText(`R${parseFloat(brand.cost).toFixed(2)}`, cx + textPad, ty);
        }

        curY += CARD_H + Math.round(W * 0.025);
      }

      // ── Download ──────────────────────────────────────────────────────────
      const link = document.createElement('a');
      link.download = `recipe-ad-${activeFormat.id}-${(recipe?.name || 'recipe').replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Check the browser console for details.');
    } finally {
      setExporting(false);
    }
  };

  const servings = recipe?.servings || 1;
  const scaledIngredients = recipe?.ingredients?.length
    ? recipe.ingredients.map((ing) => ({
        ...ing,
        amount: parseFloat(((ing.amount * servings) / (recipe.servings || 1)).toFixed(2))
      }))
    : [];

  const excludedSet = new Set(excludedIngredientNames);
  const displayTotal =
    recipeCosts != null && Object.keys(ingredientPrices).length > 0
      ? Object.entries(ingredientPrices).reduce((sum, [name, data]) => {
          if (excludedSet.has(name)) return sum;
          const brand = data?.selectedBrand || data?.availableBrands?.[0];
          return sum + (brand ? parseFloat(brand.cost) || 0 : 0);
        }, 0)
      : recipeCosts;
  const includedCount = scaledIngredients.filter((ing) => !excludedSet.has(ing.name)).length;

  return (
    <div className="marketing-page">
      <header className="marketing-nav">
        <button type="button" className="marketing-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 className="marketing-title">Marketing (Dev)</h1>
        <div className="marketing-controls">
          {/* Recipe picker */}
          <label className="marketing-label">
            Recipe
            <select
              value={selectedRecipeId}
              onChange={(e) => setSelectedRecipeId(e.target.value)}
              disabled={loadingList}
              className="marketing-select"
            >
              {loadingList && <option value="">Loading…</option>}
              {!loadingList && recipeList.length === 0 && <option value="">No recipes</option>}
              {recipeList.map((r) => (
                <option key={r._id} value={r._id}>{r.name || r._id}</option>
              ))}
            </select>
          </label>

          {/* Format picker */}
          <div className="marketing-format-group">
            {FORMATS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`marketing-format-btn${formatId === f.id ? ' active' : ''}`}
                onClick={() => setFormatId(f.id)}
                title={f.subtitle}
              >
                <span className={`marketing-format-icon marketing-format-icon--${f.id}`} />
                <span className="marketing-format-label">{f.label}</span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="marketing-export-btn"
            onClick={handleExport}
            disabled={!recipe || exporting}
          >
            {exporting ? 'Exporting…' : `Export ${activeFormat.width}×${activeFormat.height}`}
          </button>
        </div>
      </header>

      {/* Format info bar */}
      {recipe && (
        <div className="marketing-format-bar">
          <strong>{activeFormat.label}</strong> — {activeFormat.subtitle} &nbsp;·&nbsp; {activeFormat.width}×{activeFormat.height}px
        </div>
      )}

      <div className="marketing-content">
        {loadingRecipe && (
          <div className="marketing-loading">
            <LoadingSpinner size="lg" color="primary" text="Loading recipe…" />
          </div>
        )}
        {!loadingRecipe && !recipe && selectedRecipeId && (
          <div className="marketing-empty">Could not load recipe.</div>
        )}
        {!loadingRecipe && recipe && (
          /* Preview wrapper — card flows at natural height; export crops to format */
          <div className="marketing-preview-wrap">
            <div
              ref={exportRef}
              className={`marketing-card marketing-card--${formatId}`}
            >
              {/* Hero image */}
              <div className={`marketing-card-image-wrap marketing-card-image-wrap--${formatId}`}>
                <img
                  src={recipe.imageUrl
                    ? (recipe.imageUrl.includes('cloudinary.com')
                        ? recipe.imageUrl.replace('/upload/', '/upload/q_auto,f_auto/')
                        : recipe.imageUrl)
                    : DEFAULT_IMAGE}
                  alt={recipe.name}
                  className="marketing-card-image"
                />
                {/* Title overlay on story format */}
                {formatId === 'story' && (
                  <div className="marketing-story-overlay">
                    <h2 className="marketing-story-title">{recipe.name}</h2>
                    {recipe.servings && (
                      <p className="marketing-story-servings">{recipe.servings} servings</p>
                    )}
                  </div>
                )}
              </div>

              <div className="marketing-card-body">
                {formatId !== 'story' && (
                  <>
                    <h2 className="marketing-card-title">{recipe.name}</h2>
                    {recipe.servings && (
                      <p className="marketing-card-servings">{recipe.servings} servings</p>
                    )}
                  </>
                )}

                {nutritionData?.perServing && (
                  <div className="marketing-nutrition">
                    <div className="marketing-nutrition-title">Nutrition per serving</div>
                    <div className="marketing-nutrition-grid">
                      {[
                        ['Calories', nutritionData.perServing.Calories],
                        ['Protein',  nutritionData.perServing.Protein],
                        ['Carbs',    nutritionData.perServing.Carbs],
                        ['Fats',     nutritionData.perServing.Fats],
                      ].map(([key, val]) => (
                        <div key={key} className="marketing-nutrition-item">
                          <span className="marketing-nutrition-value">{formatNutritionValue(key, val)}</span>
                          <span className="marketing-nutrition-label">{key}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="marketing-prices">
                  {loadingCost && <p className="marketing-cost-loading">Loading prices…</p>}
                  {!loadingCost && displayTotal != null && (
                    <div className="marketing-total-cost">
                      <span className="marketing-cost-label">Total cost:</span>
                      <span className="marketing-cost-amount">R{Number(displayTotal).toFixed(2)}</span>
                      <span className="marketing-cost-per">
                        ({includedCount > 0 ? `R${(displayTotal / servings).toFixed(2)}/serving` : '—'})
                      </span>
                    </div>
                  )}
                  {!loadingCost && Object.keys(ingredientPrices).length > 0 && (
                    <div className="marketing-price-details">
                      <MarketingPriceDetails
                        ingredients={scaledIngredients}
                        ingredientPrices={ingredientPrices}
                        excludedIngredientNames={excludedIngredientNames}
                        onRemoveIngredient={(name) =>
                          setExcludedIngredientNames((prev) => [...prev, name])
                        }
                        onAddBackIngredient={(name) =>
                          setExcludedIngredientNames((prev) => prev.filter((n) => n !== name))
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
